import {
  guardarDadosUsuario,
  guardarDadosUsuario2,
  obterDadosUsuario2,
} from "../../../services/cookies";
import { obterComposicaoCarteira } from "../../../services/fechamento";
import { obterTickersOmexPro, OmexProTicker } from "../../../services/omexpro";
import { renovacaoToken } from "../../../services/usuario";
import { usuarioServicoFinanceiroTipo } from "../../../context/servicoFinanceiroCliente";
import type { ativoFinanceiro } from "./Ativos";

const assetColors = [
  "#f7931b",
  "#28e0c6",
  "#3982d1",
  "#75ce36",
  "#ef7c29",
  "#8aa0e9",
  "#bc6ccf",
  "#65d1b1",
  "#f3af30",
  "#4da338",
  "#d34486",
  "#94dbdd",
];

export interface CarteiraAtivoEnriquecido extends ativoFinanceiro {
  symbol: string;
  displayName: string;
  market?: OmexProTicker;
  marketAvailable: boolean;
  marketPrice?: number;
  change24hPct?: number;
  high24h?: number;
  low24h?: number;
  quoteVolume24h?: number;
  trades24h?: number;
  marketUpdatedAt?: string;
}

export interface CarteiraAtivosData {
  ativos: CarteiraAtivoEnriquecido[];
  labels: string[];
  porcentagem: number[];
  colorArray: string[];
  marketDataCount: number;
  marketUpdatedAt?: string;
}

const buildAssetColors = (totalItems: number) =>
  Array.from({ length: totalItems }, (_, index) => assetColors[index % assetColors.length]);

export const randomColor = (
  porcentagem: number[],
  _colorArray: string[],
  setColorArray: React.Dispatch<React.SetStateAction<string[]>>
) => {
  setColorArray(buildAssetColors(porcentagem.length));
};

export const normalizeAssetSymbol = (symbol: string) => {
  let normalized = (symbol || "").trim().toUpperCase().replace(/\s+/g, "");

  if (!normalized) {
    return "";
  }

  const pairParts = normalized.split(/[/:_-]+/).filter(Boolean);
  normalized = pairParts[0] || normalized;
  normalized = normalized.replace(/[^A-Z0-9]/g, "");

  const quoteAssets = ["USDT", "USDC", "BUSD", "FDUSD", "BRL", "USD"];

  for (const quoteAsset of quoteAssets) {
    if (normalized !== quoteAsset && normalized.endsWith(quoteAsset)) {
      return normalized.slice(0, -quoteAsset.length);
    }
  }

  return normalized;
};

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getCompositionFromResponse = (response: any): ativoFinanceiro[] =>
  response?.data?.resultado?.ativos ??
  response?.data?.response?.data?.resultado?.ativos ??
  response?.data?.ativos ??
  [];

const isUnauthorizedError = (error: any) =>
  error?.response?.status === 401 ||
  error?.response?.data?.codigo === 401 ||
  String(error?.message || error).includes("401");

const getRefreshTokensFromResponse = (response: any) => {
  const camposAdicionais =
    response?.data?.camposAdicionais ??
    response?.data?.resultado?.camposAdicionais ??
    response?.data?.response?.data?.camposAdicionais;

  return {
    tokenAcesso: camposAdicionais?.tokenAcesso,
    tokenRenovacao: camposAdicionais?.tokenRenovacao,
  };
};

const fetchWalletComposition = async (
  token: string,
  usuarioServicoFinanceiro: usuarioServicoFinanceiroTipo
) => {
  const response = await obterComposicaoCarteira(
    token,
    usuarioServicoFinanceiro.idServicoFinanceiro
  );

  return getCompositionFromResponse(response);
};

const fetchWalletCompositionWithRefresh = async (
  usuarioServicoFinanceiro: usuarioServicoFinanceiroTipo,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const dadosUsuario = obterDadosUsuario2();

  try {
    return await fetchWalletComposition(dadosUsuario.token, usuarioServicoFinanceiro);
  } catch (error: any) {
    if (!isUnauthorizedError(error)) {
      throw error;
    }

    try {
      const refreshed = await renovacaoToken(dadosUsuario.token, dadosUsuario.tokenRenovacao);
      const { tokenAcesso, tokenRenovacao } = getRefreshTokensFromResponse(refreshed);

      if (!tokenAcesso || !tokenRenovacao) {
        throw new Error("Token refresh response is missing tokens.");
      }

      guardarDadosUsuario(
        tokenAcesso,
        tokenRenovacao,
        dadosUsuario.email || "",
        dadosUsuario.id || ""
      );
      guardarDadosUsuario2(
        tokenAcesso,
        tokenRenovacao,
        dadosUsuario.email || "",
        dadosUsuario.id || ""
      );

      return await fetchWalletComposition(tokenAcesso, usuarioServicoFinanceiro);
    } catch (refreshError) {
      setModalDesconectar(true);
      throw refreshError;
    }
  }
};

const createTickerMap = (tickers: OmexProTicker[]) =>
  tickers.reduce((accumulator, ticker) => {
    const baseSymbol = normalizeAssetSymbol(ticker.base_symbol || ticker.symbol);
    const fullSymbol = normalizeAssetSymbol(ticker.symbol);

    if (baseSymbol) {
      accumulator[baseSymbol] = ticker;
    }

    if (fullSymbol) {
      accumulator[fullSymbol] = ticker;
    }

    return accumulator;
  }, {} as Record<string, OmexProTicker>);

const enrichAssets = async (
  ativosFinanceiros: ativoFinanceiro[]
) => {
  const symbols = ativosFinanceiros.map((asset) => normalizeAssetSymbol(asset.sigla || asset.nome));
  let tickers: OmexProTicker[] = [];

  try {
    tickers = await obterTickersOmexPro(symbols.filter((symbol) => !["BRL", "USD"].includes(symbol)));
  } catch {
    tickers = [];
  }

  const tickerMap = createTickerMap(tickers);

  return ativosFinanceiros.map((asset) => {
    const symbol = normalizeAssetSymbol(asset.sigla || asset.nome);
    const ticker = tickerMap[symbol];
    const marketPrice = ticker ? toNumber(ticker.price) : undefined;

    return {
      ...asset,
      symbol,
      displayName: ticker?.name || asset.nome || symbol,
      market: ticker,
      marketAvailable: Boolean(ticker && marketPrice !== undefined),
      marketPrice,
      change24hPct: toNumber(ticker?.change_24h_pct),
      high24h: toNumber(ticker?.high_24h),
      low24h: toNumber(ticker?.low_24h),
      quoteVolume24h: toNumber(ticker?.quote_volume_24h ?? ticker?.volume_24h),
      trades24h: toNumber(ticker?.trades_24h),
      marketUpdatedAt: ticker?.updated_at,
    };
  });
};

const buildAtivosData = async (
  composicao: ativoFinanceiro[]
): Promise<CarteiraAtivosData> => {
  const ativosFinanceiros = composicao
    .filter((asset) => asset && (asset.sigla || asset.nome))
    .sort(
      (a, b) =>
        Number(b.porcentagemDoAtivoNaCarteira || 0) -
        Number(a.porcentagemDoAtivoNaCarteira || 0)
    );

  const ativos = await enrichAssets(ativosFinanceiros);
  const labels = ativos.map((asset) => asset.symbol || asset.sigla);
  const porcentagem = ativos.map((asset) => Number(asset.porcentagemDoAtivoNaCarteira || 0));
  const marketUpdatedAt = ativos
    .map((asset) => asset.marketUpdatedAt)
    .filter(Boolean)
    .sort()
    .pop();

  return {
    ativos,
    labels,
    porcentagem,
    colorArray: buildAssetColors(ativos.length),
    marketDataCount: ativos.filter((asset) => asset.marketAvailable).length,
    marketUpdatedAt,
  };
};

export const carregarAtivosCarteira = async (
  usuarioServicoFinanceiro: usuarioServicoFinanceiroTipo,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const composicao = await fetchWalletCompositionWithRefresh(
    usuarioServicoFinanceiro,
    setModalDesconectar
  );

  return buildAtivosData(composicao);
};

export const exibirAtivos = async (
  setAtivosData: React.Dispatch<React.SetStateAction<CarteiraAtivosData>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setErroPage: React.Dispatch<React.SetStateAction<boolean>>,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>,
  usuarioServicoFinanceiro: usuarioServicoFinanceiroTipo
) => {
  setLoading(true);

  try {
    const ativosData = await carregarAtivosCarteira(usuarioServicoFinanceiro, setModalDesconectar);
    setAtivosData(ativosData);
    setErroPage(false);
  } catch {
    setErroPage(true);
  } finally {
    setLoading(false);
  }
};
